import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { datadogInterface } from '../utilities/dataDog';
import { getBrowserData } from '../utilities/browserDataExtractor';
import useAnalyticEvent from './useAnalyticEvent';

/**
 * Hook to track browser back button navigation events
 * @param options Configuration options
 * @param options.component The component name for the analytics event
 * @returns Object with method to manually track back navigation
 */
const useBackButtonHandler = (options?: { component?: string }) => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const component = options?.component || 'Navigation';
  const { recordEvent } = useAnalyticEvent();
  const previousPathRef = useRef<string>(location.pathname);

  const trackBackNavigation = useCallback(
    (from: string, to: string, isManual = false, navType: string = 'browser-back') => {
      try {
        const browserData = getBrowserData();

        datadogInterface.addAction('BackNavigation', {
          component,
          from,
          to,
          navigationType: navType,
          timestamp: new Date().toISOString(),
          manual: isManual,
          browserInfo: {
            browser: browserData.browserName,
            os: browserData.osName,
            language: browserData.language,
            screenSize: browserData.screenResolution,
            windowSize: browserData.browserResolution
          }
        });

        recordEvent('Redirected', component, 'BackButton', {
          fromPath: from,
          toPath: to,
          navigationMethod: navType,
          isManual
        });
      } catch (_error) {
        // Silent error handling in production
      }
    },
    [component, recordEvent]
  );

  useEffect(() => {
    const previousPath = previousPathRef.current;
    const currentPath = location.pathname;

    // Only track when navigation type is 'POP' (back/forward button)
    // and the path has actually changed
    if (navigationType === 'POP' && previousPath !== currentPath) {
      trackBackNavigation(previousPath, currentPath, false, 'browser-back');
    }

    // Always update the previous path reference for next comparison
    previousPathRef.current = currentPath;
  }, [location, navigationType, trackBackNavigation]);

  // Fallback for native browser back button (in case React Router misses it)
  useEffect(() => {
    const handlePopState = (_event: PopStateEvent) => {
      const currentPath = location.pathname;
      const newPath = window.location.pathname;

      trackBackNavigation(currentPath, newPath || currentPath, false, 'native-browser-back');
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [location, trackBackNavigation]);

  return {
    trackBackNavigation: (from: string, to: string) => {
      trackBackNavigation(from, to, true, 'in-app-back');
    },

    trackInAppBackButtonClick: () => {
      const from = location.pathname;

      try {
        const browserData = getBrowserData();

        datadogInterface.addAction('InAppBackButtonClick', {
          component,
          from,
          timestamp: new Date().toISOString(),
          browserInfo: browserData
        });

        recordEvent('Clicked', component, 'BackButton', {
          fromPath: from,
          navigationMethod: 'in-app-back'
        });
      } catch (_error) {
        // Silent error handling in production
      }
    }
  };
};

export default useBackButtonHandler;
